/**
 * MFA Enrollment — guides user through TOTP setup with QR code.
 * Shown after first login when MFA is not yet configured.
 */
import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'
import { useNavigate } from 'react-router-dom'

const inputClass =
  'w-full px-4 py-3 bg-surface-tertiary border border-edge rounded-lg text-ink placeholder:text-ink-subtle focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent tracking-[0.3em] text-center text-lg font-mono'

export default function MfaEnroll() {
  const navigate = useNavigate()
  const [factorId, setFactorId] = useState(null)
  const [qrCode, setQrCode] = useState(null)
  const [secret, setSecret] = useState(null)
  const [showSecret, setShowSecret] = useState(false)
  const [verifyCode, setVerifyCode] = useState('')
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [enrolling, setEnrolling] = useState(true)

  useEffect(() => {
    enrollTotp()
  }, [])

  async function enrollTotp() {
    setEnrolling(true)
    setError(null)
    try {
      const { data, error: enrollError } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: 'Authenticator App',
      })
      if (enrollError) throw enrollError
      setFactorId(data.id)
      setQrCode(data.totp.qr_code)
      setSecret(data.totp.secret)
    } catch (err) {
      setError(err.message || 'Failed to start MFA enrollment')
    } finally {
      setEnrolling(false)
    }
  }

  async function handleVerify(e) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const { data: challenge, error: challengeError } =
        await supabase.auth.mfa.challenge({ factorId })
      if (challengeError) throw challengeError

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challenge.id,
        code: verifyCode,
      })
      if (verifyError) throw verifyError

      // Do NOT call getSession() here — it deadlocks with the
      // onAuthStateChange triggered by mfa.verify().
      // Full page reload picks up the stored AAL2 session cleanly.
      window.location.replace('/dashboard')
      return
    } catch (err) {
      setError(err.message || 'Invalid code. Please try again.')
      setVerifyCode('')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center px-4 py-6 sm:py-10">
      <div className="w-full max-w-md">
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-ink">Set Up Two-Factor Authentication</h1>
          <p className="text-ink-muted mt-2">Scan the QR code with your authenticator app</p>
        </div>

        <div className="bg-surface-secondary rounded-xl p-5 sm:p-8 border border-edge-subtle">
          {error && (
            <div className="bg-danger/40 border border-danger-border rounded-lg p-3 mb-6 text-danger-fg text-sm">
              {error}
            </div>
          )}

          {enrolling ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-ink-muted">Setting up MFA...</div>
            </div>
          ) : qrCode ? (
            <>
              {/* Step 1: QR Code */}
              <div className="mb-6">
                <p className="text-sm text-ink-muted mb-4">
                  Open your authenticator app (Google Authenticator, Authy, 1Password, etc.)
                  and scan this QR code:
                </p>
                <div className="flex justify-center mb-4">
                  <div className="bg-white p-3 rounded-lg">
                    <img src={qrCode} alt="TOTP QR Code" className="w-48 h-48" />
                  </div>
                </div>

                {/* Manual entry fallback */}
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setShowSecret(!showSecret)}
                    className="text-xs text-brand hover:text-brand-secondary"
                  >
                    {showSecret ? 'Hide' : "Can't scan? Enter code manually"}
                  </button>
                  {showSecret && secret && (
                    <div className="mt-2 bg-surface-tertiary rounded-lg p-3">
                      <code className="text-xs text-ink font-mono break-all select-all">{secret}</code>
                    </div>
                  )}
                </div>
              </div>

              {/* Step 2: Verify */}
              <form onSubmit={handleVerify}>
                <label className="block text-sm font-medium text-ink-muted mb-2">
                  Enter the 6-digit code from your app to verify
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className={inputClass}
                  placeholder="000000"
                  required
                  autoFocus
                />
                <button
                  type="submit"
                  disabled={loading || verifyCode.length !== 6}
                  className="w-full mt-4 py-3 bg-brand hover:opacity-90 disabled:opacity-50 text-ink-onAccent font-semibold rounded-lg transition"
                >
                  {loading ? 'Verifying...' : 'Verify & Activate'}
                </button>
              </form>
            </>
          ) : (
            <div className="text-center py-8 text-danger-fg">
              Failed to generate QR code. Please refresh and try again.
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
